import React, { useEffect } from 'react';
import { setPageSeo, setJsonLd } from '../utils/seo';
import './TermsPage.css';

function TermsPage() {
  useEffect(() => {
    setPageSeo({
      title: 'Terms & Conditions | Binding Thread Australia',
      description: 'Read the Terms and Conditions and Refund Policy for Binding Thread. Covers our three keepsake letter collections, what\u2019s included, refunds, and how we handle your family\u2019s stories.',
      canonical: 'https://www.bindingthread.com.au/terms'
    });
    setJsonLd('ld-terms', {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Terms & Conditions",
      "description": "Terms of service, refund policy, and purchase conditions for Binding Thread keepsake letter collections.",
      "url": "https://www.bindingthread.com.au/terms",
      "isPartOf": {
        "@type": "WebSite",
        "name": "Binding Thread",
        "url": "https://www.bindingthread.com.au"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Binding Thread",
        "url": "https://www.bindingthread.com.au"
      }
    });
  }, []);

  return (
    <div className="terms-page" data-testid="terms-page">
      <div className="terms-container">
        <h1>Terms &amp; Conditions</h1>
        <p className="last-updated">Last Updated: February 14, 2026</p>

        <section className="terms-section">
          <h2>1. The Service</h2>
          <p>
            Binding Thread provides a keepsake letter service where a Loved One records a story
            via 1800 315 152 (Australia) or (877) 502-1353 (US), which is then transcribed onto archival paper, and mailed to the Subscriber.
            Letters are sold in three fixed collections \u2014 A Thread (3 letters), Woven (6 letters), or Tapestry (12 letters) \u2014 each including the Binding Thread keepsake tin and shipping.
          </p>
        </section>

        <section className="terms-section">
          <h2>2. Purchase &amp; Payment</h2>

          <h3>One-Time Payment</h3>
          <p>Each Binding Thread collection is a single one-time purchase. There is no monthly billing and no recurring charges.</p>

          <h3>What\u2019s Included</h3>
          <p>Every collection includes the Binding Thread keepsake tin and shipping. The price you see is the price you pay.</p>

          <h3>Letter Quota</h3>
          <p>Letters are produced as your Loved One shares their stories. There is no time limit on when these calls must be made \u2014 your collection remains active until all letters are delivered.</p>
        </section>

        <section className="terms-section">
          <h2>3. The &quot;Loved One&quot; Consent</h2>
          <ul>
            <li>
              By providing the contact details of a Loved One, the Subscriber represents that they
              have the authority or permission to do so.
            </li>
            <li>
              Binding Thread will send an initial &quot;Welcome SMS&quot; to the Loved One to confirm their participation.
            </li>
            <li>
              A Loved One may opt-out of the service at any time by replying &quot;STOP&quot; to any SMS.
            </li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>4. Content &amp; Recording Limits</h2>
          <p>There is no time limit on recordings. Your loved one can share as much or as little as they like.</p>

          <h3>Ownership</h3>
          <p>
            The Subscriber and Loved One retain all intellectual property rights to the stories shared.
            Binding Thread is granted a limited license to process this content for the sole purpose of fulfilling the service.
          </p>

          <h3>Sensitive Content</h3>
          <p>
            We reserve the right to refuse the production of letters containing illegal, hateful, or threatening content.
          </p>
        </section>

        <section className="terms-section">
          <h2>5. Shipping &amp; Fulfillment</h2>
          <p>We use Australia Post for all physical deliveries.</p>
          <p>
            While we take every care to ensure archival quality, Binding Thread is not responsible
            for delays or losses caused by the postal service.
          </p>
        </section>

        <section className="terms-section">
          <h2>6. Limitation of Liability</h2>
          <p>
            Binding Thread provides a sentimental service. We are not a legal &quot;vault&quot; or a formal estate planning service.
          </p>
          <p>
            We are not liable for the loss of digital audio files or physical letters once dispatched.
          </p>
        </section>

        <hr className="section-divider" />

        <h2 className="refund-title">Refund Policy</h2>

        <section className="terms-section">
          <h2>1. The &quot;Change of Heart&quot; (First 14 Days)</h2>
          <p>
            If you purchase a collection and your Loved One decides they do not wish to participate before any
            letters have been produced, we offer a <strong>full refund</strong> within the first 14 days. Simply email us and we\u2019ll take care of it.
          </p>
          <p className="note">
            Note: If the Keepsake Tin has already been dispatched, the refund will be for the letter portion only, minus the cost of the physical tin and shipping.
          </p>
        </section>

        <section className="terms-section">
          <h2>2. Non-Participation</h2>
          <p>If your Loved One does not call straight away, that\u2019s perfectly fine. Your letters wait for them.</p>
          <p>
            We send a gentle nudge after a quiet period, and we\u2019re always here if you\u2019d like to talk.
            There is no time limit on your collection \u2014 it remains yours until all letters have been delivered.
          </p>
        </section>

        <section className="terms-section">
          <h2>3. Damaged Goods</h2>
          <p>
            If a tin or a letter arrives damaged due to Australia Post handling, please send a photo to{' '}
            <a href="mailto:admin@bindingthread.com.au">admin@bindingthread.com.au</a> within 7 days.
            We will send a replacement immediately at no extra cost to you.
          </p>
          <p>
            Due to the nature of the keepsake tin, we do not offer change-of-mind refunds once the collection has been shipped.
          </p>
        </section>
      </div>
    </div>
  );
}

export default TermsPage;
